import api from "lib/axios";
import type { ApiEnvelope, PaginatedResult } from "services/apiTypes";
import { unwrapData, unwrapList } from "services/apiTypes";
import type { QuizQuestion } from "services/instructorQuizQuestionsService";

export type CourseQuiz = {
  id: number;
  course_id?: number;
  folder_id?: number;
  title: string;
  pass_degree: number;
  max_attempts: number;
  questions?: QuizQuestion[];
  created_at?: string;
  updated_at?: string;
};

export type QuizUpsertBody = {
  title: string;
  pass_degree: number;
  max_attempts: number;
};

const cache = new Map<string, CourseQuiz>();
const cacheKey = (courseId: number | string, quizId: number | string) =>
  `${courseId}:${quizId}`;

export const instructorQuizzesService = {
  async listForCourse(courseId: number | string) {
    const res = await api.get<
      ApiEnvelope<PaginatedResult<CourseQuiz>> | ApiEnvelope<{ data: CourseQuiz[] }>
    >(`/instructors/courses/${courseId}/quizzes`, { meta: { toastSuccess: false } });

    const paginated = unwrapList<CourseQuiz>(res.data as any);
    const list: CourseQuiz[] = Array.isArray(paginated.data) && (paginated.meta?.total ?? 0) >= 0
      ? paginated.data
      : ((res.data as any)?.result?.data as CourseQuiz[] | undefined) ?? [];

    for (const quiz of list) cache.set(cacheKey(courseId, quiz.id), quiz);
    return list;
  },

  async create(courseId: number | string, folderId: number | string, body: QuizUpsertBody) {
    const res = await api.post<ApiEnvelope<{ data: CourseQuiz }>>(
      `/instructors/courses/${courseId}/folders/${folderId}/quizzes`,
      body
    );
    const data = unwrapData(res.data);
    if (data) cache.set(cacheKey(courseId, data.id), data);
    return data;
  },

  async get(courseId: number | string, quizId: number | string, opts: { force?: boolean } = {}) {
    const key = cacheKey(courseId, quizId);
    if (!opts.force && cache.has(key)) return cache.get(key) as CourseQuiz;
    const res = await api.get<ApiEnvelope<{ data: CourseQuiz }>>(
      `/instructors/courses/${courseId}/quizzes/${quizId}`,
      { meta: { toastSuccess: false } }
    );
    const data = unwrapData(res.data);
    if (data) cache.set(key, data);
    return data;
  },

  async update(courseId: number | string, quizId: number | string, body: QuizUpsertBody) {
    const res = await api.put<ApiEnvelope<{ data: CourseQuiz }>>(
      `/instructors/courses/${courseId}/quizzes/${quizId}`,
      body
    );
    const data = unwrapData(res.data);
    if (data) cache.set(cacheKey(courseId, quizId), data);
    return data;
  },

  async remove(courseId: number | string, quizId: number | string) {
    const res = await api.delete<ApiEnvelope<unknown>>(
      `/instructors/courses/${courseId}/quizzes/${quizId}`
    );
    cache.delete(cacheKey(courseId, quizId));
    return { message: (res.data as any)?.message ?? "Deleted." };
  },

  invalidateQuiz(courseId: number | string, quizId: number | string) {
    cache.delete(cacheKey(courseId, quizId));
  },

  invalidateCourse(courseId: number | string) {
    for (const key of Array.from(cache.keys())) {
      if (key.startsWith(`${courseId}:`)) cache.delete(key);
    }
  },
};
